import { CSSObject } from '@emotion/react';
import React, { useMemo, useState } from 'react';
import Select, { FormatOptionLabelMeta, StylesConfig } from 'react-select';

import { reactSelectStyle } from '@/components/uiParts/Form/Select/ReactSelect/style';
import {
  ALL_STORES_OPTION_BORDER_COLOR,
  ALL_STORES_OPTION_LABEL,
  formatStoreLabel,
  matchSubsequence,
  NO_MATCHING_STORE_LABEL,
  NO_STORE_CANDIDATES_LABEL,
  STORE_FIXED_BG,
  STORE_FIXED_BORDER_COLOR,
  STORE_FIXED_COLOR,
  STORE_LOAD_ERROR_LABEL,
  STORE_MATCH_HIGHLIGHT_BG,
  STORE_MENU_MAX_HEIGHT,
  STORE_SEARCH_PLACEHOLDER,
} from '@/pages/externals/mos-pos-resend/service';
import { MosPosStore } from '@/services/MosPosOrder/type';

type Props = {
  stores: MosPosStore[];
  isLoading: boolean;
  hasError: boolean;
  value: number | null;
  onChange: (value: number | null) => void;
};

/** 「すべての店舗」固定オプション、または実店舗の候補 */
type StoreOption =
  | { kind: 'all'; label: string; value: null }
  | { kind: 'store'; label: string; value: number };

/** label のうち matchedIndices の位置の文字だけをハイライトする */
const renderHighlightedLabel = (
  label: string,
  matchedIndices: number[]
): React.ReactNode => {
  if (matchedIndices.length === 0) return label;

  const matchedSet = new Set(matchedIndices);
  return label.split('').map((char, index) =>
    matchedSet.has(index) ? (
      // eslint-disable-next-line react/no-array-index-key
      <mark
        key={index}
        style={{
          backgroundColor: STORE_MATCH_HIGHLIGHT_BG,
          fontWeight: 'bold',
        }}
      >
        {char}
      </mark>
    ) : (
      // eslint-disable-next-line react/no-array-index-key
      <React.Fragment key={index}>{char}</React.Fragment>
    )
  );
};

/** Dashboard 共通の react-select の見た目に、画面設計書 No.20 のモック固有の見た目を重ねる */
const createStyles = (
  isFixedDisplay: boolean
): StylesConfig<StoreOption, false> => ({
  ...reactSelectStyle,
  control: (baseStyles) => {
    const commonStyles = reactSelectStyle.control(baseStyles as CSSObject);
    return isFixedDisplay
      ? {
          ...commonStyles,
          backgroundColor: STORE_FIXED_BG,
          borderColor: STORE_FIXED_BORDER_COLOR,
        }
      : commonStyles;
  },
  placeholder: (baseStyles) =>
    isFixedDisplay ? { ...baseStyles, color: STORE_FIXED_COLOR } : baseStyles,
  singleValue: (baseStyles) =>
    isFixedDisplay ? { ...baseStyles, color: STORE_FIXED_COLOR } : baseStyles,
  option: (baseStyles, state) => ({
    ...baseStyles,
    ...(state.data.kind === 'all'
      ? { borderBottom: `1px solid ${ALL_STORES_OPTION_BORDER_COLOR}` }
      : {}),
    ...(state.isSelected ? { fontWeight: 'bold' } : {}),
  }),
});

/**
 * 画面設計書 No.20 の店舗検索欄。
 *
 * 候補が 1 件以下のとき、または候補の取得に失敗したときは操作不可の固定表示に
 * する。このとき onChange は呼ばれないため、検索条件の authStoreId は null の
 * まま (= 絞り込みなし) になる。
 */
export const StoreFilter: React.VFC<Props> = ({
  stores,
  isLoading,
  hasError,
  value,
  onChange,
}) => {
  const [inputValue, setInputValue] = useState('');

  const storeOptions: StoreOption[] = useMemo(
    () =>
      stores.map((store) => ({
        kind: 'store',
        label: formatStoreLabel(store),
        value: store.auth_store_id,
      })),
    [stores]
  );

  const options: StoreOption[] = useMemo(
    () => [
      { kind: 'all', label: ALL_STORES_OPTION_LABEL, value: null },
      ...storeOptions,
    ],
    [storeOptions]
  );

  const selectedOption = useMemo(
    () => storeOptions.find((option) => option.value === value) ?? null,
    [storeOptions, value]
  );

  const handleChange = (selected: StoreOption | null): void => {
    if (selected === null || selected.kind === 'all') {
      onChange(null);
      return;
    }
    onChange(selected.value);
  };

  // 閉じたときや選択時に react-select が入力を空に戻す通知 (input-blur 等) も
  // 受け取り、ハイライトの基準を filterOption の rawInput と揃える
  const handleInputChange = (newValue: string): void => {
    setInputValue(newValue);
  };

  const filterOption = (
    option: { label: string; value: string; data: StoreOption },
    rawInput: string
  ): boolean => {
    if (option.data.kind === 'all') return rawInput === '';
    return matchSubsequence(option.label, rawInput).hit;
  };

  const formatOptionLabel = (
    option: StoreOption,
    labelMeta: FormatOptionLabelMeta<StoreOption, false>
  ): React.ReactNode => {
    if (labelMeta.context !== 'menu' || option.kind === 'all') {
      return option.label;
    }
    const { matchedIndices } = matchSubsequence(option.label, inputValue);
    return renderHighlightedLabel(option.label, matchedIndices);
  };

  // 取得失敗時の stores は [] だが、紐付け拠点が 0 件という結果とは区別して表示する
  const isFixedDisplay = !isLoading && (hasError || stores.length <= 1);
  const displayValue =
    !isLoading && !hasError && stores.length === 1
      ? storeOptions[0]
      : selectedOption;
  const placeholder = (() => {
    if (isLoading) return STORE_SEARCH_PLACEHOLDER;
    if (hasError) return STORE_LOAD_ERROR_LABEL;
    if (stores.length === 0) return NO_STORE_CANDIDATES_LABEL;
    return STORE_SEARCH_PLACEHOLDER;
  })();

  return (
    <Select<StoreOption>
      inputId="mos-pos-resend-auth-store"
      // クラス名を固定してテストから × ボタン等を特定できるようにする
      classNamePrefix="mos-pos-resend-auth-store"
      value={displayValue}
      options={options}
      isLoading={isLoading}
      isDisabled={isLoading || isFixedDisplay}
      isClearable
      isSearchable
      // × は押下後に入力欄へフォーカスを戻すため、フォーカスで開くことで
      // 候補リストを「すべての店舗」から再表示する (画面設計書 No.20)
      openMenuOnFocus
      placeholder={placeholder}
      noOptionsMessage={() => NO_MATCHING_STORE_LABEL}
      filterOption={filterOption}
      formatOptionLabel={formatOptionLabel}
      onChange={handleChange}
      onInputChange={handleInputChange}
      maxMenuHeight={STORE_MENU_MAX_HEIGHT}
      // 固定表示は選択肢を開けないため、開閉を示す ▼ と区切り線を出さない
      components={
        isFixedDisplay
          ? { DropdownIndicator: null, IndicatorSeparator: null }
          : undefined
      }
      styles={createStyles(isFixedDisplay)}
    />
  );
};
